<script setup lang="ts">
import { h, reactive, ref } from "vue";
import AdminLayout from "./AdminLayout.vue";
import { AdminFetcher } from "../../api/adminFetcher";
import { toastErr, toastOk } from "../../utils/toasts";
import { closeModal, confirmModal } from "../../utils/programmingModals";
import { DataTableColumns, NButton, NDataTable, NSpace } from "naive-ui";
import { RowData } from "naive-ui/es/data-table/src/interface";

type AccessRequests = {
  id: string;
  login: string;
};

const loading = ref(true);

const requests = reactive<AccessRequests[]>([]);
function getRequests() {
  loading.value = true;
  AdminFetcher.get({
    path: "/superadmin/requests",
  })
    .then((resp: AccessRequests[]) => {
      requests.length = 0;
      requests.push(...resp);
      loading.value = false;
    })
    .catch(() => {
      toastErr("Ошибка загрузки списка запросов");
    });
}
getRequests();

const rejectRequest = async (login: string) => {
  confirmModal({
    title: "Отклонить запрос?",
    text: "После отмены запроса, он удалится и принять позднее его будет нельзя",
    okBtn: {
      text: "ОТКЛОНИТЬ",
      color: "red",
    },
    ok: async () => {
      try {
        await AdminFetcher.post({
          path: `/superadmin/reject-access`,
          payload: {
            login,
          },
        });
        getRequests();
        toastOk("Успешно");
        closeModal();
      } catch {
        toastErr("Ошибка");
        closeModal();
      }
    },
  });
};

const approveRequest = async (login: string) => {
  confirmModal({
    title: "Принять запрос?",
    text: "После принятия запроса пользователь получит доступ к админ панели",
    okBtn: {
      text: "Принять",
      color: "red",
    },
    ok: async () => {
      try {
        await AdminFetcher.post({
          path: `/superadmin/approve-access`,
          payload: {
            login,
          },
        });
        getRequests();
        toastOk("Успешно");
        closeModal();
      } catch {
        toastErr("Ошибка");
        closeModal();
      }
    },
  });
};

function createColumns() {
  const cols: DataTableColumns<RowData> = [
    {
      title: "Пользователь",
      key: "login",
      sorter: "default",
    },
    {
      title: "Действия",
      key: "actions",
      render(row: RowData) {
        return h(
          NSpace,
          {
            horizontal: true,
          },
          {
            default: () => [
              h(
                NButton,
                {
                  strong: true,
                  secondary: true,
                  type: "error",
                  onClick: () => {
                    rejectRequest(row.login);
                  },
                },
                {
                  default: () => "Отклонить",
                },
              ),
              h(
                NButton,
                {
                  strong: true,
                  secondary: true,
                  type: "success",
                  onClick: () => {
                    approveRequest(row.login);
                  },
                },
                {
                  default: () => "Принять",
                },
              ),
            ],
          },
        );
      },
    },
  ];
  return cols;
}
const columns = createColumns();
</script>

<template>
  <AdminLayout>
    <template #header>
      <h4>Запросы доступа к админ панели</h4>
    </template>
    <div class="accounts-page">
      <NDataTable
        :columns="columns"
        :data="requests"
        :loading="loading"
        :style="{
          height: '100%',
        }"
        flex-height
      ></NDataTable>
    </div>
  </AdminLayout>
</template>

<style lang="scss" scoped>
.accounts-page {
  padding: 20px;
  height: 100px;
  flex-grow: 1;
}

.accounts-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  h2 {
    color: #333;
    font-size: 1.5rem;
  }

  .create-account-btn {
    padding: 0.5rem 1rem;
    background: #5b9bd5;
    color: #fff;
    border: none;
    border-radius: 4px;
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.3s;

    &:hover {
      background: #4a8ac2;
    }
  }
}

.accounts-list {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);

  .account-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #eee;

    &:last-child {
      border-bottom: none;
    }

    .account-name {
      font-size: 1rem;
      color: #333;
    }

    .account-actions {
      display: flex;
      align-items: center;

      input[type="text"] {
        padding: 0.5rem;
        margin-right: 10px;
        font-size: 0.9rem;
        border: 0px;
        border-bottom: 1px solid #ccc;
        height: 35.5px;

        &:focus {
          outline: none;
          border-bottom: 1px solid #5b9bd5;
        }
      }

      button {
        margin-left: 10px;
        padding: 0.5rem 1rem;
        border: none;
        border-radius: 4px;
        font-size: 0.9rem;
        cursor: pointer;
        transition: background 0.3s;

        &.approve {
          background: #5b9bd5;
          color: #fff;

          &:hover {
            background: #4a8ac2;
          }
        }

        &.delete {
          background: #e74c3c;
          color: #fff;

          &:hover {
            background: #c0392b;
          }
        }
      }
    }
  }
}
</style>
